import React, { useState } from 'react';
import {
  IconCheck,
  IconChevronDown,
  IconCircleCheckFilled,
  IconCircleDashed,
  IconExternalLink,
  IconFile,
  IconFolder,
  IconLoader2,
  IconX,
} from '@tabler/icons-react';
import type { DocType } from '../../../entities/lead/model/types';
import type { CardModalFilesController } from '../model/cardModalTypes';

const DOC_BUTTONS: { type: DocType; label: string }[] = [
  { type: 'passport', label: 'Паспорт' },
  { type: 'ticket', label: 'Билет' },
  { type: 'attitude', label: 'Отношение' },
];

const TYPE_LABEL: Record<DocType, string> = {
  passport: 'Паспорт',
  ticket: 'Билет',
  attitude: 'Отношение',
};

const isImageFile = (name: string) => /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(name);
const isAudioFile = (name: string) => /\.(mp3|ogg|wav|m4a|aac|flac)$/i.test(name);

interface Props {
  files: CardModalFilesController;
}

const CardDocReadiness: React.FC<Props> = ({ files }) => {
  const [openKey, setOpenKey] = useState<DocType | 'other' | null>(null);
  const { attachments, docTypes, getFileUrl, handleSetFileType, savingFile } = files;

  const toggleOpen = (key: DocType | 'other') => setOpenKey(prev => (prev === key ? null : key));
  const hasType = (type: DocType) => Object.values(docTypes).includes(type);
  const readyCount = DOC_BUTTONS.filter(b => hasType(b.type)).length;

  // Файлы для раздела: без mp3 и без тех, что уже отмечены другим типом
  const filesFor = (key: DocType | 'other') =>
    attachments.filter(name => {
      if (isAudioFile(name)) return false;
      const t = docTypes[name];
      return key === 'other' ? !t : !t || t === key;
    });

  const otherCount = filesFor('other').length;

  const fileRow = (name: string, activeType: DocType | null) => {
    const checked = activeType != null && docTypes[name] === activeType;
    const saving = savingFile === name;
    return (
      <div className={`cm-docs-file${checked ? ' checked' : ''}`} key={name}>
        {activeType != null && (
          <button
            className={`cm-docs-cb${checked ? ' on' : ''}`}
            type="button"
            disabled={saving}
            onClick={() => handleSetFileType(name, checked ? null : activeType)}
            aria-label={checked ? 'Убрать тип' : 'Отметить тип'}
          >
            {saving
              ? <IconLoader2 size={12} stroke={2.5} className="spin" />
              : checked && <IconCheck size={12} stroke={3} />}
          </button>
        )}
        {isImageFile(name)
          ? <img className="cm-docs-thumb" src={getFileUrl(name)} alt="" loading="lazy" />
          : <span className="cm-docs-thumb cm-docs-thumb-file"><IconFile size={18} stroke={1.8} /></span>}
        <span className="cm-docs-file-name">{name}</span>
        <a
          className="cm-docs-file-open"
          href={getFileUrl(name)}
          target="_blank"
          rel="noreferrer"
          title="Открыть в новой вкладке"
        >
          <IconExternalLink size={14} stroke={2} />
        </a>
      </div>
    );
  };

  const panelTitle = openKey === 'other'
    ? 'Прочие документы'
    : openKey
      ? `Отметьте файлы — ${TYPE_LABEL[openKey]}`
      : '';

  const panelRows = openKey ? filesFor(openKey) : [];

  const emptyText = openKey === 'other'
    ? 'Нет неразмеченных документов'
    : 'Нет доступных файлов';

  return (
    <div className="cm-docs">
      <div className="cm-docs-bar">
        <div className="cm-docs-head">
          <span className="cm-docs-title">Документы</span>
          <span className="cm-docs-counter">{readyCount}/{DOC_BUTTONS.length}</span>
        </div>
        <div className="cm-docs-chips">
          {DOC_BUTTONS.map(({ type, label }) => {
            const ready = hasType(type);
            const active = openKey === type;
            return (
              <button
                key={type}
                className={`cm-doc-chip${ready ? ' ready' : ' missing'}${active ? ' active' : ''}`}
                type="button"
                onClick={() => toggleOpen(type)}
              >
                {ready
                  ? <IconCircleCheckFilled size={16} className="cm-doc-chip-ic" />
                  : <IconCircleDashed size={16} stroke={2} className="cm-doc-chip-ic" />}
                {label}
                <IconChevronDown size={13} stroke={2.5} className="cm-doc-chip-chev" />
              </button>
            );
          })}
          <button
            className={`cm-doc-chip cm-doc-chip-other${openKey === 'other' ? ' active' : ''}`}
            type="button"
            onClick={() => toggleOpen('other')}
          >
            <IconFolder size={15} stroke={2} className="cm-doc-chip-ic" />
            Прочие
            {otherCount > 0 && <span className="cm-doc-chip-count">{otherCount}</span>}
            <IconChevronDown size={13} stroke={2.5} className="cm-doc-chip-chev" />
          </button>
        </div>
      </div>

      {openKey && (
        <div className="cm-docs-panel">
          <div className="cm-docs-panel-head">
            <span>{panelTitle}</span>
            <button className="cm-docs-panel-x" type="button" onClick={() => setOpenKey(null)} aria-label="Закрыть">
              <IconX size={14} stroke={2.4} />
            </button>
          </div>
          <div className="cm-docs-list">
            {panelRows.length === 0
              ? <div className="cm-docs-empty">{emptyText}</div>
              : panelRows.map(name => fileRow(name, openKey === 'other' ? null : openKey))}
          </div>
        </div>
      )}
    </div>
  );
};

export { CardDocReadiness };
